import { zodResolver } from "@hookform/resolvers/zod";
import { Grid } from "@mui/material";
import { useRef } from "react";
import { type UseFormReturn, useForm } from "react-hook-form";

import {
  type ClientFormData,
  clientFormSchema,
} from "Clients/Forms/clientFormSchema";
import { BlFormDropdown } from "Forms/Components/BlFormDropdown";
import { BlFormInput } from "Forms/Components/BlFormInput";
import { BlPhoneInput } from "Forms/Components/BlPhoneInput";
import { useDefaultValues } from "Forms/Hooks/useDefaultValues";
import useHandleServerError from "Forms/Hooks/useHandleServerError";
import { getCountryOptions } from "Forms/Utils/EnumOptions";
import { getPostalCodeMaskRegex } from "Forms/Utils/postalCodeUtils";
import type { ClientDetailDto } from "Infrastructure/Api/Api";
import { Spacing } from "Infrastructure/Themes/Themes";
import { Resources, useResource } from "Infrastructure/Translations/Resources";

export interface ClientFormHandle {
  submit: (
    onValid: (data: ClientFormData) => void | Promise<void>,
  ) => () => void;
  reset: () => void;
}

interface ClientFormProps {
  client?: ClientDetailDto | null;
  formRef: React.RefObject<ClientFormHandle | null>;
  serverError?: Error | null;
}

export const ClientForm = ({
  client,
  formRef,
  serverError,
}: ClientFormProps): React.ReactNode => {
  const { t } = useResource();

  const internalFormRef = useRef<UseFormReturn<ClientFormData> | null>(null);

  const defaultValues = useDefaultValues(
    () => buildDefaults(client),
    [client],
    internalFormRef,
  );

  const form = useForm<ClientFormData>({
    resolver: zodResolver(clientFormSchema),
    defaultValues,
  });

  internalFormRef.current = form;

  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
    watch,
  } = form;

  useHandleServerError({
    form,
    error: serverError,
    formKey: "companyName",
    resource: Resources.ApiErrors.SaveClient,
  });

  // Expose submit/reset to parent via formRef
  formRef.current = {
    submit: (onValid) => () => handleSubmit(onValid)(),
    reset: () => reset(),
  };

  const watchedCountry = watch("country");

  return (
    <Grid container spacing={Spacing.M}>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="companyName"
          label={t(Resources.Clients.CreateClientModal.CompanyName)}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.CompanyNamePlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="companyNumber"
          label={t(Resources.Clients.CreateClientModal.CompanyNumber)}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.CompanyNumberPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="taxNumber"
          label={t(Resources.Clients.CreateClientModal.TaxNumber)}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.TaxNumberPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="email"
          label={t(Resources.Clients.CreateClientModal.Email)}
          errors={errors}
          placeholder={t(Resources.Clients.CreateClientModal.EmailPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlPhoneInput
          control={control}
          name="phone"
          errors={errors}
          label={t(Resources.Clients.CreateClientModal.Phone)}
          placeholder={t(Resources.Clients.CreateClientModal.PhonePlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormDropdown
          control={control}
          name="country"
          label={t(Resources.Clients.CreateClientModal.Country)}
          options={getCountryOptions()}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.CountryPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="address"
          label={t(Resources.Clients.CreateClientModal.Address)}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.AddressPlaceholder,
          )}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="city"
          label={t(Resources.Clients.CreateClientModal.City)}
          errors={errors}
          placeholder={t(Resources.Clients.CreateClientModal.CityPlaceholder)}
          required
        />
      </Grid>
      <Grid size={6}>
        <BlFormInput
          control={control}
          name="postalCode"
          label={t(Resources.Clients.CreateClientModal.PostalCode)}
          errors={errors}
          placeholder={t(
            Resources.Clients.CreateClientModal.PostalCodePlaceholder,
          )}
          maskProps={{ mask: getPostalCodeMaskRegex(watchedCountry) }}
          required
        />
      </Grid>
    </Grid>
  );
};

function buildDefaults(client?: ClientDetailDto | null): ClientFormData {
  if (!client) {
    return {
      companyName: "",
      companyNumber: "",
      taxNumber: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      postalCode: "",
      country: "",
    };
  }

  return {
    companyName: client.companyName,
    companyNumber: client.companyNumber,
    taxNumber: client.taxNumber,
    email: client.email ?? "",
    phone: client.phone ?? "",
    address: client.address?.street ?? "",
    city: client.address?.municipality ?? "",
    postalCode: client.address?.postalCode ?? "",
    country: client.address?.countryCode ?? "",
  };
}
